import { useState } from 'react';
import type { TenantInvoice, CreateSubscriptionDto, UpdateSubscriptionDto } from '@yawi/types';
import { Loader2 } from 'lucide-react';
import { useConfirmation } from '@/app/providers';
import { useTenantBilling } from '../../hooks/useTenantBilling';
import { TenantSubscriptionCard } from './TenantSubscriptionCard';
import { TenantUsageCard } from './TenantUsageCard';
import { TenantInvoicesTable } from './TenantInvoicesTable';
import { TenantSubscriptionModal } from './TenantSubscriptionModal';
import { MarkInvoicePaidModal } from './MarkInvoicePaidModal';

interface TenantBillingOverviewProps {
  tenantId: string;
}

export function TenantBillingOverview({ tenantId }: TenantBillingOverviewProps) {
  const { confirm } = useConfirmation();
  const {
    summary,
    invoices,
    isLoading,
    createSubscription,
    updateSubscription,
    cancelSubscription,
    generateInvoice,
    markInvoicePaid,
    cancelInvoice,
  } = useTenantBilling(tenantId);

  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [markPaidInvoice, setMarkPaidInvoice] = useState<TenantInvoice | null>(null);

  const handleCancelSubscription = async () => {
    const confirmed = await confirm({
      title: 'Cancelar Suscripción',
      message: '¿Estás seguro de cancelar la suscripción de este tenant?',
      variant: 'danger',
      confirmText: 'Cancelar suscripción',
    });
    if (confirmed) {
      await cancelSubscription();
    }
  };

  const handleSaveSubscription = async (dto: CreateSubscriptionDto | UpdateSubscriptionDto) => {
    if (summary?.subscription) {
      await updateSubscription(dto as UpdateSubscriptionDto);
    } else {
      await createSubscription(dto as CreateSubscriptionDto);
    }
  };

  const handleGenerateInvoice = async () => {
    const confirmed = await confirm({
      title: 'Generar Factura',
      message: '¿Generar una factura para el periodo actual?',
      variant: 'warning',
      confirmText: 'Generar',
    });
    if (confirmed) {
      await generateInvoice();
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-48">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Next Invoice Estimate */}
      {summary?.nextInvoiceEstimate && (
        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
          <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-2">Estimación próxima factura</h4>
          <div className="grid grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-blue-600 dark:text-blue-300">Base</p>
              <p className="font-medium text-blue-900 dark:text-blue-100">${summary.nextInvoiceEstimate.baseAmount}</p>
            </div>
            <div>
              <p className="text-blue-600 dark:text-blue-300">Excedentes</p>
              <p className="font-medium text-blue-900 dark:text-blue-100">${summary.nextInvoiceEstimate.extraCharges}</p>
            </div>
            <div>
              <p className="text-blue-600 dark:text-blue-300">Módulos</p>
              <p className="font-medium text-blue-900 dark:text-blue-100">${summary.nextInvoiceEstimate.modulesAmount}</p>
            </div>
            <div>
              <p className="text-blue-600 dark:text-blue-300">Total estimado</p>
              <p className="font-bold text-blue-900 dark:text-blue-100">${summary.nextInvoiceEstimate.totalEstimate}</p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <TenantSubscriptionCard
          subscription={summary?.subscription}
          plan={summary?.plan}
          onEdit={() => setShowSubscriptionModal(true)}
          onCancel={handleCancelSubscription}
        />
        <TenantUsageCard usage={summary?.usage} />
      </div>

      <TenantInvoicesTable
        invoices={invoices}
        onGenerate={handleGenerateInvoice}
        onMarkPaid={(inv) => setMarkPaidInvoice(inv)}
        onCancel={cancelInvoice}
      />

      {showSubscriptionModal && (
        <TenantSubscriptionModal
          subscription={summary?.subscription}
          onClose={() => setShowSubscriptionModal(false)}
          onSave={handleSaveSubscription}
        />
      )}

      {markPaidInvoice && (
        <MarkInvoicePaidModal
          invoice={markPaidInvoice}
          onClose={() => setMarkPaidInvoice(null)}
          onConfirm={markInvoicePaid}
        />
      )}
    </div>
  );
}
